<script setup>
import EmptyState from '@/components/EmptyState';
import { ref, inject } from 'vue';
import { useRequestStore } from '@/stores/request';
import { storeToRefs } from 'pinia';

const requestStore = useRequestStore();
const { search, cancelRequest } = requestStore;
const { all } = storeToRefs(requestStore);

const params = ref({
    term: '',
    type: null,
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchRequests = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchRequests();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchRequests();
};

const openCreate = () => {
    modals.show('RequestsModal', {}).then(() => {
        searchRequests();
    }).catch(() => {
        // Modal cancelled
    });
};

const openComplete = (request) => {
    modals.show('CompleteRequestModal', { props: { request } }).then(() => {
        searchRequests();
    }).catch(() => {
        // Modal cancelled
    });
};

const cancel = async (request) => {
    try {
        await cancelRequest({ hash: request.hash });
        searchRequests();
    } catch (error) {
        // The response interceptor surfaces the error toast
    }
};

const statusColor = (status) => {
    if (status === 'completed') {
        return 'success';
    }
    if (status === 'cancelled') {
        return 'neutral';
    }
    return 'accent';
};
</script>

<template>
<MainContentLayout>
    <template #title>{{ $t('label.requests') }}</template>
    <template #actions>
        <StyledButton @click="openCreate"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.new_request') }}
        </StyledButton>
    </template>
    <div class="rounded-xl border border-line bg-surface p-4 mb-4">
        <div class="grid lg:grid-cols-3 gap-x-3">
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchRequests"
                name="search"
                :label="$t('label.search')"
                :debounce-events="['input']"
                @click:clear="clearSearch" />
            <StyledDropdown
                v-model="params.type"
                clearable
                :placeholder="$t('label.type')"
                :options="[
                    { label: $t('label.to_storage'), value: 'to_storage' },
                    { label: $t('label.from_storage'), value: 'from_storage' },
                ]"
                name="type"
                @input="searchRequests" />
            <StyledDropdown
                v-model="params.status"
                clearable
                :placeholder="$t('label.status')"
                :options="[
                    { label: $t('label.open'), value: 'open' },
                    { label: $t('label.completed'), value: 'completed' },
                    { label: $t('label.cancelled'), value: 'cancelled' },
                ]"
                name="status"
                @input="searchRequests" />
        </div>
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.reference'), key: 'reference', value: item => item.reference, sortable: false },
            { title: $t('label.customer'), key: 'customer_name', value: item => item.customer_name ?? '', sortable: false },
            { title: $t('label.tire_set'), key: 'set_barcode', value: item => item.set_barcode ?? '', sortable: false },
            { title: $t('label.type'), key: 'type', value: item => item.type === 'to_storage' ? $t('label.to_storage') : $t('label.from_storage'), sortable: false },
            { title: $t('label.scheduled_for'), key: 'scheduled_for', value: item => item.scheduled_for ?? '', sortable: false },
            { title: $t('label.status'), key: 'status', value: item => item.status ?? '', sortable: false },
        ]"
        :actions="[
            { title: $t('label.complete'), icon: 'mdi-check-circle-outline', isVisible: (item) => item.status === 'open', action: (item) => openComplete(item) },
            { title: $t('label.cancel_request'), icon: 'mdi-close-circle-outline', isVisible: (item) => item.status === 'open', action: (item) => cancel(item) },
        ]"
        @table-update="tableUpdate">
        <template #[`item.reference`]="{ item }">
            <span class="font-mono text-xs font-medium">{{ item.reference }}</span>
        </template>
        <template #[`item.customer_name`]="{ item }">
            <span class="block font-semibold">{{ item.customer_name }}</span>
            <span class="text-xs text-ink-light">{{ item.vehicle_label }}</span>
        </template>
        <template #[`item.set_barcode`]="{ item }">
            <span class="font-mono text-xs">{{ item.set_barcode }}</span>
        </template>
        <template #[`item.type`]="{ item }">
            <StatusChip :color="item.type === 'to_storage' ? 'winter' : 'summer'">
                {{ item.type === 'to_storage' ? $t('label.to_storage') : $t('label.from_storage') }}
            </StatusChip>
        </template>
        <template #[`item.status`]="{ item }">
            <StatusChip :color="statusColor(item.status)">
                {{ $t('label.' + (item.status ?? 'open')) }}
            </StatusChip>
        </template>
        <template #no-data>
            <EmptyState
                icon="mdi-calendar-check-outline"
                :title="$t('label.no_requests_found')"
                :hint="$t('label.no_requests_hint')">
                <StyledButton @click="openCreate"><FontAwesomeIcon icon="plus" class="mr-1" />
                    {{ $t('label.new_request') }}
                </StyledButton>
            </EmptyState>
        </template>
    </DataTable>
</MainContentLayout>
</template>
